import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiService } from "../services/api";
import Layout from "../components/Layout";
import {
  Plus,
  LayoutDashboard,
  MoreVertical,
  Trash2,
  Edit,
  Eye,
  Loader2,
  Calendar,
  Search,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const DashboardsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [newDashboardName, setNewDashboardName] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchDashboards = async () => {
    try {
      const response = await apiService.getDashboards(token);
      setDashboards(response.data);
    } catch (error) {
      toast.error("Erro ao carregar dashboards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboards();
  }, [token]);

  const handleCreate = async () => {
    if (!newDashboardName.trim()) return;
    setCreating(true);
    try {
      const response = await apiService.createDashboard(
        {
          name: newDashboardName,
          layout_json: {},
        },
        token,
      );
      toast.success("Dashboard criado com sucesso!");
      setCreateDialogOpen(false);
      setNewDashboardName("");
      navigate(`/dashboard/${response.data.id}`);
    } catch (error) {
      toast.error("Erro ao criar dashboard");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDashboard) return;
    try {
      await apiService.deleteDashboard(selectedDashboard.id, token);
      toast.success("Dashboard excluído");
      setDeleteDialogOpen(false);
      setSelectedDashboard(null);
      fetchDashboards();
    } catch (error) {
      toast.error("Erro ao excluir dashboard");
    }
  };

  const filteredDashboards = dashboards.filter((d) =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Layout>
      <div className="animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">
              Dashboards
            </h1>
            <p className="text-slate-500 mt-1">
              Gerencie seus dashboards e visualizações
            </p>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto flex justify-center"
            data-testid="create-dashboard-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Dashboard
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar dashboards..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 max-w-md"
            data-testid="search-dashboards-input"
          />
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        ) : filteredDashboards.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <LayoutDashboard className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-heading text-lg font-medium text-slate-900 mb-2">
              {searchTerm
                ? "Nenhum dashboard encontrado"
                : "Nenhum dashboard ainda"}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchTerm
                ? "Tente uma busca diferente"
                : "Crie seu primeiro dashboard para começar"}
            </p>
            {!searchTerm && (
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Dashboard
              </Button>
            )}
          </div>
        ) : (
          /* Dashboard Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDashboards.map((dashboard, index) => (
              <div
                key={dashboard.id}
                className="card card-hover p-6 animate-fadeIn flex flex-col h-full"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                    <LayoutDashboard className="w-5 h-5 text-indigo-600" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="p-2 hover:bg-slate-100 rounded-lg shrink-0 -mr-2"
                        data-testid={`dashboard-menu-${dashboard.id}`}
                      >
                        <MoreVertical className="w-4 h-4 text-slate-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => navigate(`/dashboard/${dashboard.id}`)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Visualizar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          navigate(`/dashboard/${dashboard.id}?edit=true`)
                        }
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => {
                          setSelectedDashboard(dashboard);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex-1 min-w-0">
                  <h3
                    className="font-heading font-semibold text-slate-900 mb-2 cursor-pointer hover:text-indigo-600 transition-colors truncate"
                    onClick={() => navigate(`/dashboard/${dashboard.id}`)}
                    data-testid={`dashboard-name-${dashboard.id}`}
                    title={dashboard.name}
                  >
                    {dashboard.name}
                  </h3>
                </div>

                <div className="flex items-center gap-2 text-sm text-slate-500 mt-auto pt-4">
                  <Calendar className="w-4 h-4 shrink-0" />
                  <span className="truncate">
                    Criado em {formatDate(dashboard.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="w-[95vw] sm:w-full max-w-md">
            <DialogHeader>
              <DialogTitle>Novo Dashboard</DialogTitle>
              <DialogDescription>
                Crie um novo dashboard para visualizar seus dados
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="dashboard-name">Nome do Dashboard</Label>
              <Input
                id="dashboard-name"
                placeholder="Ex: Vendas Mensais"
                value={newDashboardName}
                onChange={(e) => setNewDashboardName(e.target.value)}
                className="mt-2"
                data-testid="new-dashboard-name-input"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0 mt-2">
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!newDashboardName.trim() || creating}
                className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
                data-testid="confirm-create-dashboard-btn"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Criar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="w-[95vw] sm:w-full max-w-md">
            <DialogHeader>
              <DialogTitle>Excluir Dashboard</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja excluir "{selectedDashboard?.name}"? Esta
                ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0 mt-4">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                data-testid="confirm-delete-dashboard-btn"
                className="w-full sm:w-auto"
              >
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DashboardsPage;
